import { api } from './api';
import { UserRole } from '../types';

export type AnnouncementType = 'info' | 'success' | 'warning' | 'error';

export interface Announcement {
  id: string;
  title: string;
  content: string;
  type: AnnouncementType;
  priority: number;
  isActive: boolean;
  // Prazdne pole = novinka pro vsechny role.
  targetRoles: UserRole[];
  // Vyplnene u automatickych novinek o letaku.
  flyerId: string | null;
  kind: 'manual' | 'flyer_published' | 'flyer_regenerated' | 'version';
  // Vraci jen /announcements/for-me - jestli si ji uzivatel uz odbyl.
  isRead?: boolean;
  expiresAt: string | null;
  createdById: string;
  createdBy?: { id: string; firstName: string; lastName: string; email: string };
  createdAt: string;
  updatedAt: string;
}

export interface AnnouncementInput {
  title: string;
  content: string;
  type?: AnnouncementType;
  /** Zarazeni novinky. Druhy o letacich zaklada aplikace sama, rucne nejdou. */
  kind?: 'manual' | 'version';
  priority?: number;
  targetRoles?: UserRole[];
  expiresAt?: string;
}

// Poradi rolí pro vyber cileni i pro vypis ve sprave.
export const ANNOUNCEMENT_ROLE_ORDER: UserRole[] = [
  'admin',
  'supplier',
  'pre_approver',
  'approver',
  'end_user',
];

export const ANNOUNCEMENT_ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Administrátor',
  supplier: 'Dodavatel',
  pre_approver: 'Předschvalovatel',
  approver: 'Schvalovatel',
  end_user: 'Prodejce',
};

// Prazdne cileni znamena "vsichni" – tak se to i vypisuje.
export function targetRolesLabel(roles?: UserRole[] | null): string {
  if (!roles || roles.length === 0) return 'Všichni';
  return ANNOUNCEMENT_ROLE_ORDER.filter((r) => roles.includes(r))
    .map((r) => ANNOUNCEMENT_ROLE_LABELS[r])
    .join(', ');
}

export const ANNOUNCEMENT_TYPE_LABELS: Record<AnnouncementType, string> = {
  info: 'Informace',
  success: 'Úspěch',
  warning: 'Varování',
  error: 'Chyba',
};

// Badge styl (v tabulce správy)
export const ANNOUNCEMENT_TYPE_BADGE: Record<AnnouncementType, string> = {
  info: 'bg-blue-100 text-blue-800',
  success: 'bg-green-100 text-green-800',
  warning: 'bg-orange-100 text-orange-800',
  error: 'bg-red-100 text-red-800',
};

// Jen podklad a text, bez okraje - pro misto, kde ramecek resi samo
// (vypis novinek pouziva stejny sedy ramecek jako box na prehledu).
export const ANNOUNCEMENT_TYPE_SURFACE: Record<AnnouncementType, string> = {
  info: 'bg-blue-50 text-blue-800',
  success: 'bg-green-50 text-green-800',
  warning: 'bg-orange-50 text-orange-800',
  error: 'bg-red-50 text-red-800',
};

// Banner styl (na dashboardu)
export const ANNOUNCEMENT_TYPE_BANNER: Record<AnnouncementType, string> = {
  info: 'bg-blue-50 border-blue-200 text-blue-800',
  success: 'bg-green-50 border-green-200 text-green-800',
  warning: 'bg-orange-50 border-orange-200 text-orange-800',
  error: 'bg-red-50 border-red-200 text-red-800',
};

/**
 * Novinka o letáku = automaticky založená aplikací (zveřejnění letáku nebo
 * přegenerování jeho PDF). Poznáme ji podle druhu, resp. vazby na leták;
 * fallback na titulek je kvůli novinkám vzniklým dřív, než pole existovala.
 */
export function isFlyerAnnouncement(a: Announcement): boolean {
  if (a.kind === 'version') return false;
  return (a.kind && a.kind !== 'manual') || !!a.flyerId || a.title.startsWith('Nový leták:');
}

/**
 * Druhy novinky, které jde založit ručně. Zbytek (novinky o letácích) zakládá
 * aplikace sama a váže je na konkrétní leták, takže se ve formuláři nenabízejí.
 */
export const ANNOUNCEMENT_KIND_LABELS: Record<'manual' | 'version', string> = {
  manual: 'Sdělení',
  version: 'Aplikace',
};

/** Novinka o novém vydání aplikace. */
export function isVersionAnnouncement(a: Announcement): boolean {
  return a.kind === 'version';
}

// Musí odpovídat backend @MaxLength v CreateAnnouncementDto.
export const ANNOUNCEMENT_CONTENT_MAX = 2000;

/**
 * Ochrana zobrazení: text novinky se před vykreslením srovná, aby stovky
 * prázdných řádků nebo koncových mezer nerozhodily stránku. Uložená data
 * to nemění, jen to, co se kreslí.
 */
export function normalizeAnnouncementText(text: string): string {
  return (text || '')
    .replace(/\r\n?/g, '\n')       // sjednotit konce řádků
    .replace(/[ \t]+$/gm, '')      // koncové mezery na řádcích
    .replace(/\n{3,}/g, '\n\n')     // 3+ prázdných řádků -> jeden
    .trim();
}

// Musí odpovídat backend DEFAULT_DISPLAY_DAYS.
const DEFAULT_DISPLAY_DAYS = 14;

export function isAnnouncementExpired(a: Announcement): boolean {
  if (!a.expiresAt) {
    return (Date.now() - new Date(a.createdAt).getTime()) / 86400000 > DEFAULT_DISPLAY_DAYS;
  }
  return new Date(a.expiresAt) < new Date();
}

export const announcementsService = {
  async getActiveAnnouncements(): Promise<Announcement[]> {
    const response = await api.get<Announcement[]>('/announcements/active');
    return response.data;
  },

  // Vsechny novinky pro prihlaseneho uzivatele vcetne uz vyprsenych (archiv).
  async getMyAnnouncements(): Promise<Announcement[]> {
    const response = await api.get<Announcement[]>('/announcements/for-me');
    return response.data;
  },

  async getAllAnnouncements(): Promise<Announcement[]> {
    const response = await api.get<Announcement[]>('/announcements');
    return response.data;
  },

  async getAnnouncement(id: string): Promise<Announcement> {
    const response = await api.get<Announcement>(`/announcements/${id}`);
    return response.data;
  },

  async createAnnouncement(data: AnnouncementInput): Promise<Announcement> {
    const response = await api.post<Announcement>('/announcements', data);
    return response.data;
  },

  async updateAnnouncement(
    id: string,
    data: Partial<AnnouncementInput> & { isActive?: boolean; expiresAt?: string | null; targetRoles?: UserRole[] },
  ): Promise<Announcement> {
    const response = await api.put<Announcement>(`/announcements/${id}`, data);
    return response.data;
  },

  // Odbyti novinky prihlasenym uzivatelem - zmizi mu z pruhu na Prehledu,
  // v prehledu /news zustava.
  async markRead(id: string): Promise<void> {
    await api.put(`/announcements/${id}/read`);
  },

  async markAllRead(): Promise<{ marked: number }> {
    const response = await api.put<{ success: boolean; marked: number }>('/announcements/read-all');
    return response.data;
  },

  async deleteAnnouncement(id: string): Promise<void> {
    await api.delete(`/announcements/${id}`);
  },

  async deactivateAnnouncement(id: string): Promise<Announcement> {
    const response = await api.put<Announcement>(`/announcements/${id}/deactivate`);
    return response.data;
  },

  async activateAnnouncement(id: string): Promise<Announcement> {
    const response = await api.put<Announcement>(`/announcements/${id}/activate`);
    return response.data;
  },
};
